import { Box, Button, IconButton, Paper, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useState, type ReactNode } from 'react'
import { MdClose, MdOpenInFull, MdOpenInNew } from 'react-icons/md'
import { signingUrlRequiresTopLevel } from '../utils/signingUrl'

/** Inline band: the panel stays in the page flow so the step keeps its context. */
const DEFAULT_HEIGHT = {
  xs: 'min(560px, calc(100dvh - 300px))',
  sm: 'min(640px, calc(100dvh - 260px))',
} as const

type SigningFrameProps = {
  /** Signing URL. While empty, `fallback` is rendered in the panel instead of the iframe. */
  src?: string
  title: string
  /** Loading / error content shown until the signing URL is available. */
  fallback?: ReactNode
  /** Panel height outside of the full-screen mode. */
  height?: Record<string, string | number> | string | number
  /** Called once the signer opens a host that refuses to be framed. The page should then poll. */
  onOpened?: () => void
}

/**
 * The band is `100dvh - 260px`, so under this viewport height it drops below the ~480px the
 * provider's own screens need — the iPhone fits its OTP step in 445px. A laptop at 125% display
 * scaling (a 1366x768 Windows screen leaves ~518px of CSS viewport) lands well under it, which is
 * why the clipping is a matter of height and not of the mobile breakpoint.
 */
const SHORT_VIEWPORT = '(max-width: 599.95px), (max-height: 740px)'

/**
 * Embedded signing panel (DocuSign).
 *
 * When the viewport is too short, the document takes the whole visual viewport: the provider page
 * is cross-origin, so anything taller than the frame is clipped with no way to scroll it from here
 * — a fixed band pushes the provider's own buttons (e.g. the OTP "Valider") out of reach.
 *
 * Full screen is a CSS switch on a single element, never a Dialog: a signing URL is consumed on
 * first load, so remounting the iframe would burn the DocuSign session. Collapsing, reopening or
 * resizing the window therefore keeps the very same iframe node.
 */
const SigningFrame = ({ src, title, fallback, height = DEFAULT_HEIGHT, onOpened }: SigningFrameProps) => {
  const theme = useTheme()
  const shortViewport = useMediaQuery(SHORT_VIEWPORT)
  const [override, setOverride] = useState<'full' | 'inline' | null>(null)
  const [openedInTab, setOpenedInTab] = useState(false)
  const opensInTab = !!src && signingUrlRequiresTopLevel(src)

  // Stay inline until the document is ready, so a loading or retry state never covers the page.
  const fullScreen = Boolean(src) && !opensInTab && (override === 'full' || (shortViewport && override !== 'inline'))

  return (
    <>
      {/* Manual escape hatch on every viewport: a window we did not anticipate can still be sized
          out of the provider's reach. */}
      {Boolean(src) && !fullScreen && !opensInTab ? (
        <Button
          variant="outlined"
          fullWidth
          startIcon={<MdOpenInFull />}
          onClick={() => setOverride('full')}
          sx={{ mb: 1 }}
        >
          Signer en plein écran
        </Button>
      ) : null}

      <Paper
        variant="outlined"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          overflow: opensInTab ? 'visible' : 'hidden',
          p: 0,
          ...(fullScreen
            ? {
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                height: '100dvh',
                zIndex: theme.zIndex.modal,
                borderRadius: 0,
                bgcolor: 'background.paper',
              }
            : { width: '100%', height: opensInTab ? 'auto' : height }),
        }}
      >
        {fullScreen ? (
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1,
              py: 0.5,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <IconButton onClick={() => setOverride('inline')} aria-label="Réduire la signature">
              <MdClose />
            </IconButton>
            <Typography variant="subtitle2" noWrap>
              {title}
            </Typography>
          </Box>
        ) : null}

        <Box
          sx={
            opensInTab
              ? { width: '100%' }
              : { position: 'relative', flex: 1, minHeight: 0, width: '100%', overflow: 'hidden' }
          }
        >
          {opensInTab && src ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, p: 3 }}>
              <Typography>
                Le document de signature ne peut pas s&apos;afficher dans cette page. Ouvrez-le dans un nouvel onglet
                pour le signer.
              </Typography>
              <Button
                component="a"
                href={src}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                startIcon={<MdOpenInNew />}
                onClick={() => {
                  setOpenedInTab(true)
                  onOpened?.()
                }}
              >
                {openedInTab ? 'Rouvrir la page de signature' : 'Ouvrir la page de signature'}
              </Button>
              {openedInTab ? (
                <Typography variant="body2" color="text.secondary">
                  Signez dans l&apos;onglet qui vient de s&apos;ouvrir, puis revenez ici : cette page se met à jour dès
                  que la signature est enregistrée.
                </Typography>
              ) : null}
            </Box>
          ) : src ? (
            <iframe
              src={src}
              title={title}
              style={{ display: 'block', width: '100%', height: '100%', border: 'none' }}
            />
          ) : (
            fallback
          )}
        </Box>
      </Paper>
    </>
  )
}

export default SigningFrame
